<!-- ═══════════════════════════════════════════════════════════
     SIMPLE DOCUMENT UPLOAD
     Upload bar (Browse / Capture) + file grid (view/download/delete)
     No metadata form — files are saved the moment they're picked.
══════════════════════════════════════════════════════════════ -->

<div class="sdu-toolbar">
  <span class="sdu-toolbar__title">
    <i class="bi bi-paperclip me-1"></i>Documents
    <span class="sdu-toolbar__badge" *ngIf="documents.length > 0">{{ documents.length }}</span>
  </span>

  <div class="sdu-toolbar__actions">
    <button class="btn btn-sm btn-outline-primary" type="button" [disabled]="uploading"
            (click)="browseInput.click()">
      <i class="bi bi-folder2-open me-1"></i>Browse
    </button>
    <button class="btn btn-sm btn-outline-primary" type="button" [disabled]="uploading"
            (click)="openCapture()">
      <i class="bi bi-camera-fill me-1"></i>Capture
    </button>

    <input #browseInput type="file" hidden multiple (change)="onBrowseSelected($event)">
  </div>
</div>

<!-- Uploading indicator -->
<div class="sdu-uploading" *ngIf="uploading">
  <span class="spinner-border spinner-border-sm me-2"></span>Uploading…
</div>

<!-- Loading -->
<div class="sdu-loading" *ngIf="loading && !uploading">
  <span class="spinner-border spinner-border-sm me-2"></span>Loading documents…
</div>

<!-- Empty state -->
<div class="sdu-empty-state" *ngIf="!loading && documents.length === 0">
  <div class="sdu-empty-state__icon"><i class="bi bi-folder2-open"></i></div>
  <p class="sdu-empty-state__title">No documents yet</p>
  <p class="sdu-empty-state__sub">Browse a file or capture a photo to add one</p>
</div>

<!-- File grid -->
<div class="sdu-file-grid" *ngIf="documents.length > 0">
  <div class="sdu-file-card" *ngFor="let doc of documents" (click)="viewDocument(doc)">

    <div class="sdu-icon-area" [ngClass]="getFileIconClass(doc)">
      <i [class]="getFileIcon(doc)"></i>
    </div>

    <div class="sdu-info">
      <p class="sdu-name" [title]="doc.docName">{{ doc.docName }}</p>
      <p class="sdu-meta">
        <span class="sdu-ext-badge">{{ getFileExt(doc) }}</span>
        <span class="sdu-date" *ngIf="doc.createdOn">{{ formatDate(doc.createdOn) }}</span>
      </p>
    </div>

    <div class="sdu-actions" (click)="$event.stopPropagation()">
      <button class="sdu-btn sdu-btn--view" title="View" type="button" (click)="viewDocument(doc)">
        <i class="bi bi-eye-fill"></i>
      </button>
      <button class="sdu-btn sdu-btn--download" title="Download" type="button" (click)="downloadDocument(doc, $event)">
        <i class="bi bi-download"></i>
      </button>
      <button class="sdu-btn sdu-btn--delete" title="Delete" type="button" (click)="deleteDocument(doc, $event)">
        <i class="bi bi-trash3-fill"></i>
      </button>
    </div>

  </div>
</div>

<!-- Preview modal -->
<div class="sdu-preview-modal" *ngIf="previewOpen">
  <div class="sdu-preview-modal__backdrop" (click)="closePreview()"></div>
  <div class="sdu-preview-modal__dialog">

    <div class="sdu-preview-modal__header">
      <span class="sdu-preview-modal__title"><i class="bi bi-eye me-2"></i>Document Preview</span>
      <button class="sdu-preview-modal__close" (click)="closePreview()" type="button">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="sdu-preview-modal__body">
      <img *ngIf="previewType === 'image'" [src]="previewUrl" class="sdu-preview-modal__img" />
      <iframe *ngIf="previewType === 'pdf' || previewType === 'office'" [src]="previewUrl" class="sdu-preview-modal__frame"></iframe>
      <video *ngIf="previewType === 'video'" controls class="sdu-preview-modal__video">
        <source [src]="previewUrl" />
      </video>
      <div *ngIf="previewType === 'other'" class="sdu-preview-modal__unsupported">
        <i class="bi bi-file-earmark-arrow-down"></i>
        <p>No preview available for this file type.</p>
        <button type="button" class="btn btn-sm btn-primary" (click)="downloadPreviewFile()">
          <i class="bi bi-download me-1"></i>Download File
        </button>
      </div>
    </div>

  </div>
</div>

<!-- Webcam capture modal -->
<div class="sdu-capture-modal" *ngIf="captureModalOpen">
  <div class="sdu-capture-modal__backdrop" (click)="closeCaptureModal()"></div>
  <div class="sdu-capture-modal__dialog">

    <div class="sdu-capture-modal__header">
      <span class="sdu-capture-modal__title"><i class="bi bi-camera-fill me-2"></i>Capture Photo</span>
      <button class="sdu-preview-modal__close" (click)="closeCaptureModal()" type="button">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <div class="sdu-capture-modal__body">
      <video #videoRef *ngIf="!capturedPhotoPreview" autoplay playsinline muted class="sdu-capture-video"></video>
      <img *ngIf="capturedPhotoPreview" [src]="capturedPhotoPreview" class="sdu-capture-preview-img" />
      <canvas #canvasRef hidden></canvas>
    </div>

    <div class="sdu-capture-modal__footer">
      <ng-container *ngIf="!capturedPhotoPreview">
        <button class="btn btn-primary" type="button" (click)="takePhoto()">
          <i class="bi bi-camera-fill me-1"></i>Take Photo
        </button>
      </ng-container>
      <ng-container *ngIf="capturedPhotoPreview">
        <button class="btn btn-secondary" type="button" (click)="retakePhoto()">Retake</button>
        <button class="btn btn-primary" type="button" (click)="useCapturedPhoto()">Use Photo</button>
      </ng-container>
    </div>

  </div>
</div>
